import { computed, inject, signal } from "@angular/core";
import { Participant } from "app/core/interfaces/participant.interface";
import { ParticipantsV2Service } from "app/modules/davesa/apps/participants/participants-v2.service";
import { BroadcasterV2Service } from "app/core/services/v2-refactored/broadcaster-v2.service";
import { SignatureWorkflowService, SignatureStepResponseDto, SignatureStepRoleResponseDto } from "app/core/services/signature-workflow.service";
import { createInjectable } from "ngxtension/create-injectable";
import { Subject } from "rxjs";

export const ClinicNowV2Service = createInjectable(() => {
  const _participantsV2Service = inject(ParticipantsV2Service);
  const _broadcasterV2Service = inject(BroadcasterV2Service);
  const _signatureWorkflowService = inject(SignatureWorkflowService);
  const participants = signal<Participant[] | null>(null);
  const clinicBoard = signal<any[] | null>(null);
  const loading = signal<boolean>(false);
  const error = signal<string | null>(null);
  
  // Cache for signature workflow configurations by study OID
  const workflowCache = new Map<string, any>();

  // Add broadcasting mechanism for cross-component synchronization
  const dataUpdated = new Subject<void>();
  
  // Observable for components to subscribe to data changes
  const dataUpdated$ = dataUpdated.asObservable();

  // Format assigned personnel by signature workflow order with labels (like visit-appointment)
  const formatAssignedPersonnelByWorkflow = async (visitData: any, studyOid: string): Promise<any[]> => {
    try {
      // Get signature_assignments from visit data (from API get-appointment-by-study)
      const signatureAssignments = visitData.signature_assignments || [];
      
      if (!signatureAssignments || signatureAssignments.length === 0) {
        return [];
      }
      
      // Get signature workflow configuration to get step labels
      let workflow;
      if (workflowCache.has(studyOid)) {
        workflow = workflowCache.get(studyOid);
      } else {
        try {
          workflow = await _signatureWorkflowService.getWorkflowConfiguration(studyOid);
          if (workflow) {
            workflowCache.set(studyOid, workflow);
          }
        } catch (error) {
          console.warn('Failed to load signature workflow:', error);
          workflow = null;
        }
      }
      
      if (!workflow || !workflow.steps || workflow.steps.length === 0) {
        // No workflow, return empty
        return [];
      }
      
      // Sort steps by stepOrder
      const sortedSteps = [...workflow.steps].sort((a: SignatureStepResponseDto, b: SignatureStepResponseDto) => a.stepOrder - b.stepOrder);
      
      // Map signature_assignments to steps
      const stepsWithPersonnel: any[] = [];
      
      sortedSteps.forEach((step: SignatureStepResponseDto) => {
        const stepNames: string[] = [];
        
        if (step.allowedRoles && Array.isArray(step.allowedRoles)) {
          step.allowedRoles.forEach((role: SignatureStepRoleResponseDto) => {
            const roleId = role.delegationRoleId || (role as any).delegation_role_id;
            
            // Find matching signature assignment
            signatureAssignments.forEach((assignment: any) => {
              const assignmentRoleId = assignment.delegation_role_id || assignment.role_id;
              
              if (roleId && assignmentRoleId && roleId === assignmentRoleId && assignment.user) {
                // Extract user name (first + middle + last, suffixName)
                const firstName = assignment.user.first_name || '';
                const middleName = assignment.user.middle_name || '';
                const lastName = assignment.user.last_name || '';
                // suffixName is object { id, name } - get .name
                const suffixName = assignment.user.suffixName?.name || '';
                const namePart = [firstName, middleName, lastName].filter(n => n).join(' ').trim();
                const fullName = suffixName ? `${namePart}, ${suffixName}` : namePart;
                
                if (fullName && !stepNames.includes(fullName)) {
                  stepNames.push(fullName);
                }
              }
            });
          });
        }
        
        // Only add step if it has personnel assigned
        if (stepNames.length > 0) {
          stepsWithPersonnel.push({
            label: step.label,
            names: stepNames
          });
        }
      });
      
      return stepsWithPersonnel;
    } catch (error) {
      console.error('Error formatting assigned personnel by workflow:', error);
      return [];
    }
  };

  const getAllClinicVisits = async (studyOid?: string): Promise<any[]> => {
    try {
      loading.set(true);
      error.set(null);
      
      // Use getAppointmentsByStudy for clinic appointments (uses get-appointment-by-study API)
      const response = await _participantsV2Service.getAppointmentsByStudy(studyOid);
      
      participants.set(response);
      
      const rows = await Promise.all(response.map(async (visitData: any) => {
        // Map subject_clinic data - the API returns 'subject_clinic' directly
        const subjectClinic = visitData.subject_clinic;
        const studyOidForVisit = visitData.study?.oid || visitData.study_oid;
        
        // Format assigned personnel by signature workflow (like visit-appointment)
        const assignedPersonnelSteps = await formatAssignedPersonnelByWorkflow(visitData, studyOidForVisit);
        
        const mappedVisit = {
          ...visitData,
          // IMPORTANT: Preserve the original 'id' field (numeric database ID) for check-in API
          id: visitData.id,
          subject_id: visitData.subject?.subject_id,
          assigned_personnel_steps: assignedPersonnelSteps, // Steps with labels from signature workflow
          study_number: visitData.study?.number,
          visit_name: visitData.visit_name || visitData.study_schedule_visit?.name,
          // Fix arrived/departed mapping with proper time formatting and date context
          arrived: subjectClinic?.arrived_at ? new Date(subjectClinic.arrived_at) : null,
          departed: subjectClinic?.departed_at ? new Date(subjectClinic.departed_at) : null,
          // Preserve appointment_start_date for scheduled time display (already in ISO format)
          appointment_start_date: visitData.appointment_start_date,
          // Ensure subject_clinic is properly mapped for check out operations
          subject_clinic: subjectClinic,
        };
        
        return mappedVisit;
      }));

      clinicBoard.set(rows);
      
      // Broadcast data change to all components
      dataUpdated.next();
      
      return rows;
    }
    catch (err) {
      console.error("Error in getAllClinicVisits:", err);
      error.set(err.message || 'Failed to load clinic visits');
      throw err;
    }
    finally {
      loading.set(false);
    }
  };

  const refresh = async (studyOid?: string): Promise<void> => {
    await getAllClinicVisits(studyOid);
  };

  const checkIn = async (data: {subject_visit_id: number}): Promise<any> => {
    try {
      loading.set(true);
      const response = await _participantsV2Service.checkIn(data);
      
      // After successful check-in, refresh data and broadcast change
      await refresh();
      
      return response;
    } catch (err) {
      console.error('Error checking in subject:', err);
      // Don't set error state here - let component handle it with modal
      // error.set(err.message || 'Failed to check in subject');
      throw err;
    } finally {
      loading.set(false);
    }
  };

  const checkOut = async (data: {oid: string}): Promise<any> => {
    try {
      loading.set(true);
      const response = await _participantsV2Service.checkOut(data);
      
      // After successful check-out, refresh data and broadcast change
      await refresh();
      
      return response;
    } catch (err) {
      console.error('Error checking out subject:', err);
      // Don't set error state here - let component handle it
      // error.set(err.message || 'Failed to check out subject');
      throw err;
    } finally {
      loading.set(false);
    }
  };

  const listenForUpdates = () => {
    // Return observable for components to subscribe to
    return dataUpdated$;
  };

  // Method to manually trigger data refresh from external components
  const triggerRefresh = async (studyOid?: string) => {
    await refresh(studyOid);
  };

  return {
    participants: computed(() => participants()),
    clinicBoard: computed(() => clinicBoard()),
    loading: computed(() => loading()),
    error: computed(() => error()),
    getAllClinicVisits,
    refresh,
    checkIn,
    checkOut,
    listenForUpdates,
    triggerRefresh,
    dataUpdated$,
  };
});
